<!-- Utilization_Timeline.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-gray-900">Utilization History</h3>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-blue-50 border-b border-blue-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-blue-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-blue-800 leading-relaxed">{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="events.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'clipboard-list']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No utilization history found</p>
    </div>

    <!-- Timeline -->
    <div v-else class="p-4">
      <!-- Year Filter -->
      <div v-if="availableYears.length > 0" class="mb-3 pb-2.5 border-b border-gray-200">
        <div class="flex flex-col sm:flex-row sm:items-center gap-2">
          <span class="text-xs text-gray-600">Year:</span>
          <div class="flex flex-wrap gap-1 flex-1">
            <button
              v-for="year in availableYears"
              :key="year"
              @click="selectedYear = year"
              :class="[
                'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                selectedYear === year
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
              ]"
            >
              {{ year }}
            </button>
          </div>
          <span class="text-xs text-gray-500">
            ({{ filteredEvents.length }} {{ filteredEvents.length === 1 ? 'event' : 'events' }})
          </span>
        </div>
      </div>

      <!-- Type Filter -->
      <div v-if="availableTypes.length > 1" class="mb-3 pb-2.5 border-b border-gray-200">
        <div class="flex flex-col sm:flex-row sm:items-start gap-2">
          <span class="text-xs text-gray-600 sm:mt-1">Type:</span>
          <div class="flex flex-wrap gap-1 flex-1">
            <button
              @click="selectedType = null"
              :class="[
                'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                selectedType === null
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
              ]"
            >
              All
            </button>
            <button
              v-for="t in availableTypes"
              :key="t"
              @click="selectedType = t"
              :class="[
                'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                selectedType === t
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
              ]"
            >
              {{ formatType(t) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Visit filters -->
      <div class="mb-3 pb-2.5 border-b border-gray-200 space-y-2">
        <div class="flex flex-col sm:flex-row sm:items-center gap-2">
          <span class="text-xs text-gray-600">Provider type:</span>
          <div class="flex flex-wrap gap-1">
            <button
              v-for="opt in providerTypeOptions"
              :key="opt.value"
              @click="providerType = opt.value"
              :class="[
                'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                providerType === opt.value
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
              ]"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select
            v-model="providerFilter"
            class="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
          >
            <option value="">All providers</option>
            <option v-for="p in providerOptions" :key="p" :value="p">{{ p }}</option>
          </select>
          <input
            v-model.trim="cptQuery"
            type="text"
            placeholder="CPT / HCPCS (e.g. 99214, G0439)"
            class="text-xs border border-gray-300 rounded-md px-2 py-1"
          />
          <input
            v-model.trim="dxQuery"
            type="text"
            placeholder="ICD-10 (e.g. E11, I10)"
            class="text-xs border border-gray-300 rounded-md px-2 py-1"
          />
        </div>
        <div v-if="hasVisitFilters" class="flex items-center gap-2 text-xs text-gray-500">
          <span>{{ filteredEvents.length }} of {{ eventsAfterType.length }} events match</span>
          <button class="text-blue-600 hover:underline" @click="clearVisitFilters">Clear</button>
        </div>
      </div>

      <div v-if="filteredEvents.length === 0" class="text-center py-6 text-xs text-gray-500">
        No events match these filters
      </div>

      <!-- Timeline Content -->
      <div class="relative">
        <div class="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div class="space-y-1.5">
          <div v-for="(item, index) in filteredEvents" :key="index"
               class="relative flex items-start gap-2.5">

            <div :class="[
              'w-7 h-7 rounded-full flex items-center justify-center z-10 shrink-0',
              getTypeStyles(item._type).bg
            ]">
              <FontAwesomeIcon
                :icon="getTypeIcon(item._type)"
                :class="['text-xs', getTypeStyles(item._type).text]"
              />
            </div>

            <div class="flex-1 bg-white rounded border border-gray-200 px-2.5 py-1.5 hover:shadow-sm transition-shadow">
              <div class="flex justify-between items-start gap-2">
                <h4 class="font-medium text-gray-900 text-xs">{{ formatType(item._type) }}</h4>
                <span class="text-xs text-gray-500 shrink-0">{{ formatDate(item._date) }}</span>
              </div>

              <div v-if="item._fields && item._fields.length" class="mt-0.5 space-y-0.5 text-xs text-gray-600">
                <div v-for="(f, fi) in item._fields" :key="fi">
                  <span class="font-medium">{{ f.label }}:</span>
                  <span class="ml-1" :class="{ sensitive: f.sensitive }">{{ f.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'Utilization_Timeline',
  components: { FontAwesomeIcon },
  props: {
    claims: { type: Array, default: () => [] },
    adtEpisodes: { type: Array, default: () => [] },
    encounters: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      selectedYear: null,
      selectedType: null,
      providerType: 'all',
      providerFilter: '',
      cptQuery: '',
      dxQuery: '',
      providerTypeOptions: [
        { value: 'all', label: 'All' },
        { value: 'pcp', label: 'Primary care' },
        { value: 'specialist', label: 'Specialist' },
      ],
    };
  },
  computed: {
    events() {
      const items = [];

      // Claims → utilization events. Each card is a list of {label, value}
      // "fields" so different claim types can show what's actually meaningful to
      // them (pharmacy=drug, professional=procedure+provider, inpatient=DRG+dates,
      // etc.) rather than a fixed Reason/Facility/Disposition triple.
      for (const c of this.claims) {
        const type = this.claimTypeLabel(c.CLM_TYPE_CD, c.CLM_TYPE_LABEL);
        if (!type) continue;
        const conditions = (c.conditions || []).map(d => d.description || d.code).filter(Boolean);
        const meds = (c.medications || []).filter(Boolean);
        const procs = (c.procedures || []).filter(Boolean);
        const pracs = (c.practitioners || []).filter(pr => pr && (pr.name || pr.specialty));
        const prac = pracs[0] || {};
        const cpts = (c.procedure_codes || []).filter(Boolean);
        const dxCodes = (c.conditions || []).map(d => d.code).filter(Boolean);
        const provider = prac.name || '';
        const specialty = prac.specialty || '';
        const isPharmacy = /pharmac/i.test(type);

        const fields = [];
        // Service window (from → thru) when a thru date exists and differs.
        if (c.CLM_FROM_DT && c.CLM_THRU_DT && c.CLM_THRU_DT !== c.CLM_FROM_DT) {
          fields.push({ label: 'Dates', value: `${this.dateOnly(c.CLM_FROM_DT)} → ${this.dateOnly(c.CLM_THRU_DT)}` });
        }
        if (isPharmacy && meds.length) {
          fields.push({ label: 'Medication', value: meds.slice(0, 3).join('; '), sensitive: true });
        }
        if (!isPharmacy && procs.length) {
          fields.push({ label: 'Procedure', value: procs.slice(0, 3).join('; ') });
        }
        if (!isPharmacy && cpts.length) {
          fields.push({ label: 'CPT/HCPCS', value: cpts.slice(0, 6).join(', ') });
        }
        if (conditions.length) {
          fields.push({ label: 'Reason', value: conditions.slice(0, 3).join('; ') });
        }
        if (dxCodes.length) {
          fields.push({ label: 'ICD-10', value: dxCodes.slice(0, 6).join(', ') });
        }
        if (provider) {
          fields.push({ label: 'Provider', value: provider + (specialty ? ` (${specialty})` : ''), sensitive: true });
        }
        if (c.facility) {
          fields.push({ label: 'Facility', value: c.facility, sensitive: true });
        }
        if (c.DGNS_DRG_CD && c.DGNS_DRG_CD !== '0000') {
          fields.push({ label: 'DRG', value: c.DGNS_DRG_CD });
        }
        const paid = this.money(c.CLM_PMT_AMT);
        if (paid) {
          fields.push({ label: 'Paid', value: paid });
        }

        items.push({
          _type: type,
          _date: c.CLM_FROM_DT || '',
          _fields: fields,
          _providers: pracs.map(pr => pr.name).filter(Boolean),
          _providerClass: this.providerClass(pracs),
          _cpts: cpts,
          _procText: procs.join(' | ').toLowerCase(),
          _dx: dxCodes,
          _dxText: (c.conditions || []).map(d => d.description || '').join(' | ').toLowerCase(),
        });
      }

      // ADT episodes
      for (const e of this.adtEpisodes) {
        const fields = [];
        if (e.EPISODE_START) fields.push({ label: 'Start', value: this.formatDateTime(e.EPISODE_START) });
        if (e.EPISODE_END) fields.push({ label: 'End', value: this.formatDateTime(e.EPISODE_END) });
        if (e.DX_CODES) fields.push({ label: 'Reason', value: e.DX_CODES });
        if (e.FACILITY) fields.push({ label: 'Facility', value: e.FACILITY, sensitive: true });
        if (e.DISCHARGE_DISPOSITION) fields.push({ label: 'Disposition', value: e.DISCHARGE_DISPOSITION });
        items.push({
          _type: 'ADT',
          _date: e.EPISODE_START || '',
          _fields: fields,
          _providers: [],
          _providerClass: null,
          _cpts: [],
          _procText: '',
          _dx: String(e.DX_CODES || '').split(/[,;|\s]+/).filter(Boolean),
          _dxText: String(e.DX_CODES || '').toLowerCase(),
        });
      }

      // EHR Encounters
      for (const e of this.encounters) {
        const fields = [];
        if (e.CLINICIAN) fields.push({ label: 'Provider', value: e.CLINICIAN, sensitive: true });
        if (e.LOCATION) fields.push({ label: 'Facility', value: e.LOCATION, sensitive: true });
        items.push({
          _type: 'ENCOUNTER',
          _date: e.ENCOUNTER_DATE || '',
          _fields: fields,
          _providers: e.CLINICIAN ? [e.CLINICIAN] : [],
          _providerClass: null,
          _cpts: [],
          _procText: '',
          _dx: [],
          _dxText: '',
        });
      }

      items.sort((a, b) => (b._date || '').localeCompare(a._date || ''));
      return items;
    },

    availableYears() {
      const years = [...new Set(
        this.events
          .filter(e => e._date)
          .map(e => new Date(e._date).getFullYear())
      )];
      return years.sort((a, b) => b - a);
    },

    // Events in the selected year (before the type filter) — drives both the
    // type chips and the final list, so the chips only show types present in view.
    eventsInYear() {
      if (!this.selectedYear) return this.events;
      return this.events.filter(e => {
        if (!e._date) return false;
        return new Date(e._date).getFullYear() === this.selectedYear;
      });
    },

    availableTypes() {
      const types = [...new Set(this.eventsInYear.map(e => e._type).filter(Boolean))];
      // Stable, clinically-sensible ordering; unknown types fall to the end.
      const order = [
        'PRIMARY CARE', 'INPATIENT', 'OUTPATIENT', 'ED', 'ADT',
        'HOME HEALTH', 'HOSPICE', 'DME', 'ENCOUNTER', 'PHARMACY',
      ];
      return types.sort((a, b) => {
        const ia = order.indexOf(a); const ib = order.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
    },

    eventsAfterType() {
      if (!this.selectedType) return this.eventsInYear;
      return this.eventsInYear.filter(e => e._type === this.selectedType);
    },

    providerOptions() {
      const names = new Set();
      for (const e of this.eventsAfterType) {
        if (this.providerType !== 'all' && e._providerClass !== this.providerType) continue;
        for (const n of e._providers || []) names.add(n);
      }
      return [...names].sort((a, b) => a.localeCompare(b));
    },

    hasVisitFilters() {
      return this.providerType !== 'all' || !!this.providerFilter || !!this.cptQuery || !!this.dxQuery;
    },

    filteredEvents() {
      let list = this.eventsAfterType;
      if (this.providerType !== 'all') {
        list = list.filter(e => e._providerClass === this.providerType);
      }
      if (this.providerFilter) {
        list = list.filter(e => (e._providers || []).includes(this.providerFilter));
      }
      const cptTerms = this.terms(this.cptQuery);
      if (cptTerms.length) {
        list = list.filter(e => cptTerms.some(t =>
          (e._cpts || []).some(c => c.toLowerCase().startsWith(t)) || (t.length > 2 && e._procText.includes(t))
        ));
      }
      const dxTerms = this.terms(this.dxQuery);
      if (dxTerms.length) {
        list = list.filter(e => dxTerms.some(t => {
          const bare = t.replace(/\./g, '');
          return (e._dx || []).some(c => c.toLowerCase().replace(/\./g, '').startsWith(bare))
            || (t.length > 2 && e._dxText.includes(t));
        }));
      }
      return list;
    }
  },
  watch: {
    availableYears: {
      immediate: true,
      handler(years) {
        if (years.length && !this.selectedYear) {
          this.selectedYear = years[0];
        }
      }
    },
    providerOptions(options) {
      if (this.providerFilter && !options.includes(this.providerFilter)) {
        this.providerFilter = '';
      }
    },
    availableTypes(types) {
      // If the current type filter isn't present in the new view (e.g. after a
      // year change), clear it so the list doesn't silently go empty.
      if (this.selectedType && !types.includes(this.selectedType)) {
        this.selectedType = null;
      }
    }
  },
  methods: {
    terms(query) {
      return String(query || '').toLowerCase().split(/[,;\s]+/).map(t => t.trim()).filter(Boolean);
    },
    providerClass(pracs) {
      const specialties = pracs.map(pr => pr.specialty).filter(Boolean);
      if (!specialties.length) return null;
      const pcp = /family|general practice|internal medicine|geriatric|primary care|preventive|pediatric/i;
      return specialties.some(sp => pcp.test(sp)) ? 'pcp' : 'specialist';
    },
    clearVisitFilters() {
      this.providerType = 'all';
      this.providerFilter = '';
      this.cptQuery = '';
      this.dxQuery = '';
    },
    claimTypeLabel(code, label) {
      if (label) return label;
      const map = { '1': 'INPATIENT', '2': 'OUTPATIENT', '3': 'PRIMARY CARE', '4': 'HOME HEALTH', '5': 'HOSPICE', 'D': 'PHARMACY' };
      return map[code] || '';
    },
    getTypeIcon(type) {
      const icons = {
        'PRIMARY CARE': ['fas', 'stethoscope'],
        'INPATIENT': ['fas', 'hospital'],
        'OUTPATIENT': ['fas', 'building'],
        'ED': ['fas', 'ambulance'],
        'HOME HEALTH': ['fas', 'house-medical'],
        'HOSPICE': ['fas', 'bed'],
        'DME': ['fas', 'wheelchair'],
        'PHARMACY': ['fas', 'pills'],
        'ADT': ['fas', 'exchange-alt'],
        'ENCOUNTER': ['fas', 'calendar-check'],
      };
      return icons[type] || ['fas', 'notes-medical'];
    },
    getTypeStyles(type) {
      const styles = {
        'PRIMARY CARE': { bg: 'bg-blue-100', text: 'text-blue-600' },
        'INPATIENT': { bg: 'bg-red-100', text: 'text-red-600' },
        'OUTPATIENT': { bg: 'bg-teal-100', text: 'text-teal-600' },
        'ED': { bg: 'bg-orange-100', text: 'text-orange-600' },
        'HOME HEALTH': { bg: 'bg-green-100', text: 'text-green-600' },
        'HOSPICE': { bg: 'bg-purple-100', text: 'text-purple-600' },
        'DME': { bg: 'bg-amber-100', text: 'text-amber-600' },
        'PHARMACY': { bg: 'bg-pink-100', text: 'text-pink-600' },
        'ADT': { bg: 'bg-gray-200', text: 'text-gray-700' },
        'ENCOUNTER': { bg: 'bg-indigo-100', text: 'text-indigo-600' },
      };
      return styles[type] || { bg: 'bg-gray-100', text: 'text-gray-600' };
    },
    formatType(type) {
      const formatted = {
        'PRIMARY CARE': 'Primary Care Visit',
        'INPATIENT': 'Inpatient Admission',
        'OUTPATIENT': 'Outpatient Visit',
        'ED': 'Emergency Department',
        'HOME HEALTH': 'Home Health',
        'HOSPICE': 'Hospice',
        'DME': 'DME',
        'PHARMACY': 'Pharmacy Fill',
        'ADT': 'ADT Episode',
        'ENCOUNTER': 'EHR Encounter',
      };
      return formatted[type] || type;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const today = new Date();
      const diffDays = Math.floor((today - date) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    dateOnly(dateString) {
      if (!dateString) return '';
      const m = String(dateString).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return dateString;
      const d = new Date(+m[1], +m[2] - 1, +m[3]);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    money(v) {
      if (v === null || v === undefined || v === '') return '';
      const n = Number(v);
      if (!isFinite(n) || n === 0) return '';
      return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      // ADT timestamps are timezone-NAIVE wall-clock strings (the HL7 ingestor
      // stores facility-local time with the zone stripped). Parsing with
      // new Date(iso) applies the browser offset and can shift the calendar day
      // (an evening event rolling to "tomorrow"). Parse the literal Y/M/D H:M
      // components so the value shown always equals the stored wall-clock.
      const m = String(dateString).match(
        /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/
      );
      if (!m) {
        // Date-only or unexpected format — fall back to a plain date render.
        const dm = String(dateString).match(/(\d{4})-(\d{2})-(\d{2})/);
        if (!dm) return dateString;
        const d = new Date(+dm[1], +dm[2] - 1, +dm[3]);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      const [, y, mo, d, hh, mi] = m;
      const dt = new Date(+y, +mo - 1, +d, +hh, +mi);
      return dt.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    }
  }
};
</script>
